import { apolloClientAtom } from "@/admin/graphql/apolloClientAtom.tsx";
import { type ApiClient, getResponseData, type TypedFetchResponse } from "@/modules/api/apiClient.ts";
import { raiseRequestToast } from "@/modules/notifications/toast.tsx";
import type { Store } from "@/utils/jotai/types.ts";
import type { DefaultError, QueryClient, QueryKey } from "@tanstack/query-core";
import type { UseMutationOptions } from "@tanstack/react-query";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import invariant from "tiny-invariant";

interface AdminMutationOptions<TData, TVariables> extends UseMutationOptions<TData, DefaultError, TVariables, unknown> {
    /**
     * Optional function that makes the request (e.g. `apiClient.GET`).
     *
     * Only use when no further processing of the response is needed.
     * Otherwise, use {@link mutationFn}.
     */
    requestFn?: (variables: TVariables) => Promise<TypedFetchResponse<TData>>;
    /** Get query key to invalidate */
    invalidateQueryKey?: ((variables: TVariables) => QueryKey) | QueryKey;
    invalidateMultiple?: (variables: TVariables) => QueryKey[];

    /** Entity name for toast */
    entity: string;
    /** Operation name for toast */
    operation: string;
}

/**
 * Utility function for admin mutations.
 *
 * Automatically shows a toast and invalidates a query key.
 */
export function adminMutation<TData, TVariables>(
    fn: (apiClient: ApiClient, queryClient: QueryClient) => AdminMutationOptions<TData, TVariables>,
) {
    return (
        apiClient: ApiClient,
        queryClient: QueryClient,
        store: Store,
    ): UseMutationOptions<TData, DefaultError, TVariables, unknown> => {
        const { invalidateQueryKey, invalidateMultiple, entity, operation, requestFn, ...base } = fn(
            apiClient,
            queryClient,
        );

        invariant(!requestFn || !base.mutationFn, "Cannot provide both requestFn and mutationFn");

        return {
            mutationKey: [`${entity}/${operation}`],
            mutationFn: requestFn
                ? async (variables) => {
                      return getResponseData(await requestFn(variables)) satisfies TData;
                  }
                : undefined,

            ...base,

            async onSettled(_data, err, variables, _context) {
                const prefix = `${entity} - ${operation}`;
                raiseRequestToast(err, {
                    noNotification: true,
                    success: `${prefix}: Success`,
                    error: `${prefix}: Error`,
                });

                if (invalidateQueryKey) {
                    const queryKey =
                        typeof invalidateQueryKey === "function" ? invalidateQueryKey(variables) : invalidateQueryKey;
                    await queryClient.invalidateQueries({ queryKey });
                }

                if (invalidateMultiple) {
                    const queryKeys = invalidateMultiple(variables);
                    await Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
                }

                const apolloClient = store.get(apolloClientAtom);

                if (apolloClient) {
                    // `discardPromise` since we do not need to wait for the refetch to complete - the GraphQL tables will automatically display loading states while the new data is loading
                    discardPromise(apolloClient.refetchQueries({ include: "active" }));
                    // TODO: Only re-fetch specific queries
                }

                await base.onSettled?.(_data, err, variables, _context);
            },
        };
    };
}
